import type { AnimalKind } from "../game/animals";
import { DebouncedSaver, type PersistentStore } from "../persistence";
import { type AnimalMetrics, type MetricsSnapshot } from "./snapshot";

export interface MetricsTrackerOptions {
  readonly saveDelayMs: number;
  readonly playTimeFlushIntervalSeconds: number;
}

export class MetricsTracker {
  private readonly saver: DebouncedSaver<MetricsSnapshot>;
  private readonly flushIntervalSeconds: number;
  private sessionsStarted: number;
  private totalPlaySeconds: number;
  private readonly perAnimalSessions: Record<AnimalKind, number>;
  private readonly perAnimalPlaySeconds: Record<AnimalKind, number>;
  private secondsSinceLastSchedule = 0;

  constructor(
    private readonly store: PersistentStore<MetricsSnapshot>,
    options: MetricsTrackerOptions,
  ) {
    if (
      !Number.isFinite(options.playTimeFlushIntervalSeconds) ||
      options.playTimeFlushIntervalSeconds <= 0
    ) {
      throw new RangeError(
        `MetricsTracker playTimeFlushIntervalSeconds must be a positive finite number, got ${String(options.playTimeFlushIntervalSeconds)}`,
      );
    }
    this.flushIntervalSeconds = options.playTimeFlushIntervalSeconds;
    this.saver = new DebouncedSaver(store, { delayMs: options.saveDelayMs });

    const initial = this.store.load();
    this.sessionsStarted = initial.sessionsStarted;
    this.totalPlaySeconds = initial.totalPlaySeconds;
    this.perAnimalSessions = {
      seal: initial.perAnimal.seal.sessions,
      penguin: initial.perAnimal.penguin.sessions,
      fish: initial.perAnimal.fish.sessions,
    };
    this.perAnimalPlaySeconds = {
      seal: initial.perAnimal.seal.playSeconds,
      penguin: initial.perAnimal.penguin.playSeconds,
      fish: initial.perAnimal.fish.playSeconds,
    };
  }

  snapshot(): MetricsSnapshot {
    const entry = (animal: AnimalKind): AnimalMetrics => ({
      sessions: this.perAnimalSessions[animal],
      playSeconds: this.perAnimalPlaySeconds[animal],
    });
    return {
      sessionsStarted: this.sessionsStarted,
      totalPlaySeconds: this.totalPlaySeconds,
      perAnimal: {
        seal: entry("seal"),
        penguin: entry("penguin"),
        fish: entry("fish"),
      },
    };
  }

  recordSessionStart(animal: AnimalKind): void {
    this.sessionsStarted += 1;
    this.perAnimalSessions[animal] += 1;
    this.scheduleSave();
  }

  addPlayTime(animal: AnimalKind, deltaSeconds: number): void {
    if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) return;
    this.totalPlaySeconds += deltaSeconds;
    this.perAnimalPlaySeconds[animal] += deltaSeconds;
    this.secondsSinceLastSchedule += deltaSeconds;
    if (this.secondsSinceLastSchedule >= this.flushIntervalSeconds) {
      this.scheduleSave();
    }
  }

  flush(): void {
    if (this.secondsSinceLastSchedule > 0) {
      this.scheduleSave();
    }
    this.saver.flush();
  }

  dispose(): void {
    this.saver.cancel();
  }

  private scheduleSave(): void {
    this.secondsSinceLastSchedule = 0;
    this.saver.schedule(this.snapshot());
  }
}
